import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL + "api/notification";
const BASE_URL2 = process.env.REACT_APP_API_URL + "api/notify-preference";
// ─────────────────────────────────────────────
// 🔹 Async Thunks
// ─────────────────────────────────────────────

// Fetch all drafts
export const fetchDrafts = createAsyncThunk(
  "notifications/fetchDrafts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/get-all-notifications`, { params: { ...params, status: 1 } });
      return {
        docs: res.data.result || [],
        pagination: res.data.pagination || { totalDocs: 0, totalPages: 1, page: 1, limit: 10 }
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch drafts");
    }
  }
);

export const addCustomNotification = createAsyncThunk(
  "notifications/addCustomNotification",
  async (data, { rejectWithValue }) => {
    try {
      const isForm = typeof FormData !== "undefined" && data instanceof FormData;
      const res = await axios.post(`${BASE_URL}/add`, data, isForm ? { headers: { "Content-Type": "multipart/form-data" } } : undefined);
      return res.data.result;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to create notification");
    }
  }
);

// Send a single draft
export const sendNotification = createAsyncThunk(
  "notifications/sendNotification",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/send/${id}`);
      return res.data.result; // expecting updated sent item
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to send notification");
    }
  }
);

// Delete a draft
export const deleteDraft = createAsyncThunk(
  "notifications/deleteDraft",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`${BASE_URL}/delete/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete draft");
    }
  }
);

// Fetch sent notifications
export const fetchSentNotifications = createAsyncThunk(
  "notifications/fetchSentNotifications",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/get-all-notifications`, { params: { ...params, status: 2 } });
      return {
        docs: res.data.result || [],
        pagination: res.data.pagination || { totalDocs: 0, totalPages: 1, page: 1, limit: 10 }
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch sent notifications");
    }
  }
);

// Fetch preference data
export const fetchNotificationPreferences = createAsyncThunk(
  "notifications/fetchNotificationPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const typeMap = {
        ev_news: 1,
        new_launches: 2,
        offers_discounts: 3,
        upcoming_vehicles: 4,
      };

      const requests = Object.entries(typeMap).map(([key, type]) =>
        axios
          .get(`${BASE_URL2}/get?alert_type=${type}&sortBy=1`)
          .then((res) => ({ key, data: res.data }))
      );

      const settled = await Promise.allSettled(requests);
      const aggregated = {};
      settled.forEach((r) => {
        if (r.status === "fulfilled") {
          const { key, data } = r.value;
          aggregated[key] = data;
        }
      });

      if (Object.keys(aggregated).length === 0) {
        throw new Error("No preference data available");
      }

      return aggregated;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Failed to fetch preferences"
      );
    }
  }
);

// Get notification details (for edit modal)
export const fetchNotificationDetails = createAsyncThunk(
  "notifications/fetchNotificationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/notification-details/${id}`);
      return res.data.result;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch details");
    }
  }
);

// Update notification
export const editNotification = createAsyncThunk(
  "notifications/editNotification",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE_URL}/edit/${id}`, data);
      return res.data.result;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update notification");
    }
  }
);


// ─────────────────────────────────────────────
// 🔹 Slice
// ─────────────────────────────────────────────
const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    drafts: [],
    sent: [],
    draftsPagination: { totalDocs: 0, totalPages: 1, page: 1, limit: 10 },
    sentPagination: { totalDocs: 0, totalPages: 1, page: 1, limit: 10 },
    preferences: {},
    status: {
      fetchDrafts: "idle",
      send: "idle",
      delete: "idle",
      fetchSent: "idle",
      preferences: "idle",
      edit: "idle",
      create: "idle",
    },
    error: null,
  },

  reducers: {
    // local UI-level updates
    addDraft: (state, action) => {
      state.drafts.unshift(action.payload);
    },
    updateDraft: (state, action) => {
      const index = state.drafts.findIndex((d) => (d.id || d._id) === (action.payload.id || action.payload._id));
      if (index !== -1) state.drafts[index] = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder

      // ─────────────── fetchDrafts ───────────────
      .addCase(fetchDrafts.pending, (state) => {
        state.status.fetchDrafts = "loading";
        state.error = null;
      })
      .addCase(fetchDrafts.fulfilled, (state, action) => {
        state.status.fetchDrafts = "succeeded";
        state.drafts = Array.isArray(action.payload?.docs) ? action.payload.docs : [];
        state.draftsPagination = action.payload?.pagination || { totalDocs: 0, totalPages: 1, page: 1, limit: 10 };
      })
      .addCase(fetchDrafts.rejected, (state, action) => {
        state.status.fetchDrafts = "failed";
        state.error = action.payload;
      })

      // ─────────────── sendNotification ───────────────
      .addCase(sendNotification.pending, (state) => {
        state.status.send = "loading";
      })
      .addCase(sendNotification.fulfilled, (state, action) => {
        state.status.send = "succeeded";
        if (action.payload) {
          // remove from drafts and push to sent
          state.drafts = state.drafts.filter((d) => (d.id || d._id) !== (action.payload.id || action.payload._id));
          state.sent.unshift(action.payload);
        }
      })
      .addCase(sendNotification.rejected, (state, action) => {
        state.status.send = "failed";
        state.error = action.payload;
      })

      // ─────────────── deleteDraft ───────────────
      .addCase(deleteDraft.pending, (state) => {
        state.status.delete = "loading";
      })
      .addCase(deleteDraft.fulfilled, (state, action) => {
        state.status.delete = "succeeded";
        state.drafts = state.drafts.filter((d) => (d.id || d._id) !== (action.payload.id || action.payload._id));
      })
      .addCase(deleteDraft.rejected, (state, action) => {
        state.status.delete = "failed";
        state.error = action.payload;
      })

      // ─────────────── fetchSentNotifications ───────────────
      .addCase(fetchSentNotifications.pending, (state) => {
        state.status.fetchSent = "loading";
      })
      .addCase(fetchSentNotifications.fulfilled, (state, action) => {
        state.status.fetchSent = "succeeded";
        state.sent = Array.isArray(action.payload?.docs) ? action.payload.docs : [];
        state.sentPagination = action.payload?.pagination || { totalDocs: 0, totalPages: 1, page: 1, limit: 10 };
      })
      .addCase(fetchSentNotifications.rejected, (state, action) => {
        state.status.fetchSent = "failed";
        state.error = action.payload;
      })

      .addCase(addCustomNotification.pending, (state) => {
        state.status.create = "loading";
      })
      .addCase(addCustomNotification.fulfilled, (state, action) => {
        state.status.create = "succeeded";
        const item = action.payload;
        if (!item) return;
        if (Number(item.status) === 1) {
          state.sent.unshift(item);
          state.drafts = state.drafts.filter((d) => (d.id || d._id) !== (item.id || item._id));
        } else {
          state.drafts.unshift(item);
        }
      })
      .addCase(addCustomNotification.rejected, (state, action) => {
        state.status.create = "failed";
        state.error = action.payload;
      })

      // ─────────────── fetchNotificationPreferences ───────────────
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.status.preferences = "loading";
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.status.preferences = "succeeded";
        state.preferences = action.payload || {};
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.status.preferences = "failed";
        state.error = action.payload;
      })

      // ─────────────── editNotification ───────────────
      .addCase(editNotification.pending, (state) => {
        state.status.edit = "loading";
      })
      .addCase(editNotification.fulfilled, (state, action) => {
        state.status.edit = "succeeded";
        const idx = state.drafts.findIndex((d) => (d.id || d._id) === (action.payload.id || action.payload._id));
        if (idx !== -1) state.drafts[idx] = action.payload;
      })
      .addCase(editNotification.rejected, (state, action) => {
        state.status.edit = "failed";
        state.error = action.payload;
      });

  },
});

export const { addDraft, updateDraft, setActiveTab } = notificationsSlice.actions;
export default notificationsSlice.reducer;
